import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { addDays, DateOnly } from '../../core';
import { TreatmentProgress } from '../../models';
import { BaseComponent, ConvertDatePipe, ReplacePipe } from '../../shared';


/**
 * Component for treatment progress bar of Plan Settings page.
 */
@Component({
	selector: 'tsp-treatment-progress-bar',
	imports: [
		FormsModule,
		ReplacePipe,
		ConvertDatePipe,
		NgbProgressbarModule
	],
	templateUrl: './treatment-progress-bar.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class TreatmentProgressBarComponent extends BaseComponent {

	isReadonly = input<boolean>(false);

	progress = input<TreatmentProgress>();
	consolidationDays = output<number | undefined>();
	isValid = output<boolean>();

	frameId = computed(() => this.progress()?.frameId);
	latency = computed(() => ({ days: this.progress()?.latencyDays, date: this.progress()?.surgeryDate, size: 0 } as BarItem));
	correction = computed(() => ({ days: this.progress()?.correctionDays, date: this.progress()?.startDate, size: 0 } as BarItem));
	consolidation = signal<BarItem>({ days: 0, size: 0, date: new DateOnly() });
	consolidationDate = computed(() => addDays(this.consolidation().date, this.consolidation().days));

	isConsolidationValid = computed(() => this.consolidation().days == 0 || (this.consolidation().days > this.DAYS_MIN && this.consolidation().days <= this.DAYS_MAX));

	readonly DAYS_MIN = 0;
	readonly DAYS_MAX = 2000;
	readonly MIN_SIZE = 10;
	readonly PARTIAL_SIZE = 100;
	readonly REMOVAL_SIZE = 15;
	readonly TOTAL_SIZE = this.PARTIAL_SIZE + this.REMOVAL_SIZE;
	readonly WRONG_KEYS = ['-', '+', 'e', 'E', '.', ','];

	private readonly initConsolidation = effect(() => {
		if (this.progress()) {
			untracked(() => this.consolidation.set({ days: this.progress()!.consolidationDays, date: this.progress()!.startConsolidationDate, size: 0 }));
		}
	});

	private readonly updateCondalidationDays = effect(() => this.consolidationDays.emit(this.consolidation()?.days));

	private readonly checkIfValid = effect(() => this.isValid.emit(this.isConsolidationValid()));

	private readonly updateSize = effect(() => {
		const total = this.latency().days + this.correction().days + this.consolidation().days;
		const steps = [this.latency, this.correction, this.consolidation];
		steps.sort((a, b) => a().days - b().days);
		steps[0]().size = this._calcSize(steps[0]().days, total);
		steps[1]().size = this._calcSize(steps[1]().days, total);
		steps[2]().size = this.PARTIAL_SIZE - steps[0]().size - steps[1]().size;
	});

	private _calcSize(days: number, totDays: number): number {
		const size = Math.round(days * this.PARTIAL_SIZE / totDays);
		return size > this.MIN_SIZE ? size : this.MIN_SIZE;
	}

	/**
	 * Update consolidation days.
	 */
	update(val: number): void {
		this.consolidation.set({ ...this.consolidation(), days: val });
	}

	/**
	 * Decrement consolidation days.
	 */
	sub(): void {
		if (!this.isReadonly()) {
			this.consolidation.update(v => ({ ...v, days: v.days <= this.DAYS_MIN ? this.DAYS_MIN : v.days - 1 }));
		}
	}

	/**
	 * Increment consolidation days.
	 */
	add(): void {
		if (!this.isReadonly()) {
			this.consolidation.update(v => ({ ...v, days: v.days >= this.DAYS_MAX ? this.DAYS_MAX : v.days + 1 }));
		}
	}

	/**
	 * Check keyboard event.
	 */
	checkKey(event: KeyboardEvent): boolean {
		const val = (event.target as HTMLInputElement).value;
		if (val?.length > 3) event.preventDefault();
		return !this.WRONG_KEYS.includes(event.key);
	}

	/**
	 * Check paste event.
	 */
	checkPaste(event: ClipboardEvent): boolean {
		const val = event.clipboardData?.getData('text');
		if (val && val.length > 3) event.preventDefault();
		return !this.WRONG_KEYS.some(k => val?.includes(k));
	}

}

export interface BarItem {
	days: number;
	size: number;
	date: DateOnly;
}